"use client";

import React from "react";
import { CircleCheck, ExternalLink, FileCheck, GraduationCap, Scale, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Service = {
  title: string;
  icon: LucideIcon;
  objective: string;
  deliverables: string[];
  livrables: string;
  note?: string;
  lien?: {
    label: string;
    href: string;
  };
};
const services: Service[] = [{
  title: "Conformité légale et réglementaire",
  icon: Scale,
  objective: "Vérifier la conformité de votre activité aux exigences du Code du travail et des textes applicables au CPF, et corriger les écarts.",
  deliverables: ["Diagnostic de conformité\u00a0: analyse de vos documents et pratiques (convention et contrat de formation, CGV, contrats de sous-traitance, règlement intérieur, dossiers formateurs, supports de communication, BPF) au regard du Code du travail et des textes applicables au CPF.", "Identification des écarts\u00a0: repérage des points de non-conformité et évaluation des risques au regard des contrôles applicables.", "Plan d'actions correctif\u00a0: actions priorisées pour régulariser votre situation et documenter vos pratiques.", "Revue documentaire\u00a0: vérification de vos documents au regard des mentions et exigences du Code du travail et du RNQ (conventions, contrats de formation à titre individuel, CGV, contrats de sous-traitance et charte qualité – indicateur\u00a027 du RNQ au 1er\u00a0novembre 2026 –, règlement intérieur, certificats de réalisation), et mise à disposition de modèles intégrant ces exigences.", "Dossier de vigilance sous-traitants\u00a0: pièces à obtenir à la signature puis périodiquement (art.\u00a0L.\u00a08222-1 du Code du travail), en anticipation de l'extension au maître d'ouvrage prévue par l'article\u00a095 de la loi du 25\u00a0juin 2026.", "Communication\u00a0: mention du numéro de déclaration d'activité sous la forme réglementaire et revue des supports au regard des six rubriques de l'article\u00a0L.\u00a06352-13 du Code du travail."],
  livrables: "Rapport de diagnostic, plan d'actions priorisé, modèles de documents actualisés au regard des textes en vigueur à la date de livraison."
}, {
  title: "Veille réglementaire",
  icon: FileCheck,
  objective: "Rester informé des évolutions législatives et réglementaires impactant votre activité de formation.",
  deliverables: ["Veille réglementaire ciblée\u00a0: surveillance des évolutions législatives et réglementaires impactant votre activité (lois, décrets, arrêtés, circulaires, instructions des financeurs, jurisprudence).", "Analyse et vulgarisation des textes\u00a0: traduction des évolutions réglementaires en impacts concrets sur votre organisation et vos pratiques.", "Recommandations d'actions\u00a0: identification des actions à mener pour adapter vos pratiques aux nouvelles exigences.", "Formation des équipes\u00a0: sensibilisation de vos collaborateurs aux évolutions réglementaires et à leurs impacts opérationnels."],
  livrables: "Dans le cadre de l'abonnement Effet Perform\u00a0: newsletters trimestrielles, notes d'actualité ponctuelles et visioconférences thématiques.",
  lien: {
    label: "Consulter un extrait de la newsletter de l'automne 2026 (PDF)",
    href: "https://effet-conseil.com/wp-content/uploads/2026/10/20261005_effet-conseil_extrait-newsletter-effet-perform-t4-2026.pdf"
  }
}, {
  title: "Accompagnement à la déclaration d'activité",
  icon: ShieldCheck,
  objective: "Accompagner les nouveaux prestataires (formation, bilan de compétences, VAE, apprentissage) dans la déclaration de leur activité auprès de la DREETS (direction régionale de l'économie, de l'emploi, du travail et des solidarités).",
  deliverables: ["Analyse de votre projet\u00a0: vérification que votre activité entre bien dans le champ de la formation professionnelle et nécessite une déclaration d'activité.", "Vérification préalable des motifs de refus\u00a0: nouveaux motifs issus de la loi du 25\u00a0juin 2026 (antécédents du dirigeant, locaux adaptés pour l'apprentissage).", "Constitution du dossier de déclaration\u00a0: aide à la collecte des pièces justificatives (première convention de formation, description de l'action), dans les trois mois suivant la conclusion de la première convention ou du premier contrat de formation.", "Dépôt du dossier\u00a0: accompagnement au dépôt du dossier en ligne.", "Conseil post-déclaration\u00a0: bilan pédagogique et financier annuel et risque de caducité, plan comptable adapté, conditions d'exonération de TVA, règlement intérieur à établir dans les trois mois du début d'activité."],
  livrables: "Dossier de déclaration complet et calendrier des obligations de la première année."
}, {
  title: "Ateliers",
  icon: GraduationCap,
  objective: "Donner à vos équipes la maîtrise du cadre juridique applicable à votre activité.",
  deliverables: ["Atelier «\u00a0Cadre juridique de la formation professionnelle\u00a0»\u00a0: socle commun à tous les organismes prestataires, à jour de la loi du 25\u00a0juin 2026.", "Atelier complémentaire «\u00a0Bilan de compétences\u00a0»\u00a0: spécificités juridiques et opérationnelles.", "Atelier complémentaire «\u00a0VAE\u00a0»\u00a0: parcours du candidat et obligations du certificateur.", "Atelier complémentaire «\u00a0Apprentissage\u00a0»\u00a0: spécificités juridiques et opérationnelles de l'action de formation par apprentissage.", "Format\u00a0: en intra-entreprise, en présentiel ou à distance."],
  livrables: "Support remis aux participants et feuille de route de mise en conformité priorisée."
}];
function ServiceCard({
  service,
  index
}: {
  service: Service;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="overflow-hidden rounded-xl border border-[#d6cfc4]/60 bg-[#F5F0EB] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
        <CardContent className="p-8 lg:p-10">
          {/* Titre + icône */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary shrink-0">
              <service.icon className="w-7 h-7" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
              {service.title}
            </h3>
          </div>

          {/* Objectif */}
          <div className="mb-6">
            <p className="text-lg font-bold text-gray-800 mb-1">Objectif</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
              {service.objective}
            </p>
          </div>

          {/* L'accompagnement */}
          <div className="mb-6">
            <p className="text-lg font-bold text-gray-800 mb-3">
              L'accompagnement
            </p>
            <ul className="space-y-4">
              {service.deliverables.map((deliverable, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-1" />
                  <span className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
                    {deliverable}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Livrables */}
          <div>
            <p className="text-lg font-bold text-gray-800 mb-1">Livrables</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)] font-semibold">
              {service.livrables}
            </p>
            {service.lien && <a href={service.lien.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-base font-semibold text-[hsl(343,80%,35%)] hover:underline underline-offset-4">
                {service.lien.label}
                <ExternalLink className="size-4" aria-hidden="true" />
              </a>}
            {service.note && <p className="mt-4 text-sm italic leading-relaxed !text-[hsl(210,10%,23%)]/80">
                {service.note}
              </p>}
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function LesAccompagnements() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="les-accompagnements" className="bg-background relative overflow-hidden ![padding:60px_0px_128px_0px]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-accent/10 blur-[120px]" />
      </div>

      <div className="container max-w-7xl mx-auto px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl font-bold mb-6 !text-[hsl(210,10%,23%)] lg:text-5xl">
            Les accompagnements en conformité réglementaire
          </h2>
          <p className="text-xl leading-relaxed !text-[hsl(210,10%,23%)] md:text-2xl">
            Des accompagnements sur mesure pour une activité conforme et prête
            pour un contrôle
          </p>
        </div>

        <div className="flex flex-col gap-8">
          {services.map((service, index) => <ServiceCard key={index} index={index} service={service} />)}
        </div>
      </div>
    </section>;
}